import { useSelector } from "react-redux";

import leftArrow from "../../Assets/HourlyData/leftArrow.svg";
import { Link } from "react-router-dom";
import {
  extractTimeWithAMPM,
  getFormattedDateTime,
} from "../../AllFunctions/formattedDateAndTime";
import logoAndTitleFunction from "../../AllFunctions/logoAndTitleFunction";
import LocationSelector from "../Common/LocationSelector.jsx";

// Safe value helpers
const safeFixed = (val, digits = 1) =>
  val != null && !isNaN(val) ? Number(val).toFixed(digits) : "N/A";
const safeTrunc = (val) =>
  val != null && !isNaN(val) ? Math.trunc(val) : "N/A";
const safePct = (val, digits = 1) =>
  val != null && !isNaN(val) ? (val * 100).toFixed(digits) : "N/A";

function HourlyForcastTable() {
  const { hourlyWeatherData } = useSelector((store) => store.dashboard);

  return (
    <div className="flex flex-col h-full bg-primaryBgColor dark:bg-darkModePrimaryBgColor p-4 custom-scrollbar overflow-y-auto">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row justify-between items-center mb-6 bg-cardBgColor dark:bg-darkModeSecondaryBgColor rounded-lg border border-gray-200 dark:border-white/10 shadow-sm p-4 gap-4">
        <Link to="/dashboard">
          <div className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-white/5 rounded-lg text-blackPrimaryText dark:text-darkModeBlackPrimaryText font-bold border border-slate-200 dark:border-white/10 hover:bg-slate-50 dark:hover:bg-white/10 transition-all shadow-sm">
            <img src={leftArrow} alt="Back" className="w-4 h-4 dark:invert" />
            <span>Dashboard</span>
          </div>
        </Link>
        <h1 className="font-bold text-blackPrimaryText dark:text-darkModeBlackPrimaryText uppercase tracking-widest hidden sm:block">
          Hourly Forecast
        </h1>
        <LocationSelector />
      </div>

      {/* Date Display */}
      <div className="flex justify-between items-center px-2 mb-4">
        <p className="text-blackPrimaryText dark:text-darkModeBlackPrimaryText text-sm opacity-70">
          {getFormattedDateTime().date}
        </p>
      </div>

      <div className="flex-1">
        <div className="w-full mx-auto px-4 lg:px-8 mt-4">
          <div className="rounded-2xl overflow-x-auto custom-scrollbar shadow-2xl bg-cardBgColor dark:bg-darkModeCardBgColor border border-gray-200 dark:border-white/10 mb-12">
            <table className="w-full min-w-[1500px] border-collapse text-left">
              <thead className="bg-slate-100 dark:bg-slate-800 text-blackPrimaryText dark:text-darkModeBlackPrimaryText">
                <tr className="border-b border-slate-200 dark:border-slate-700">
                  <th className="px-4 py-4 text-base font-black whitespace-nowrap">Time</th>
                  <th className="px-4 py-4 text-base font-black whitespace-nowrap">Condition</th>
                  <th className="px-4 py-4 text-base font-black whitespace-nowrap text-center">Avg °C</th>
                  <th className="px-4 py-4 text-base font-black whitespace-nowrap text-center">Min / Max</th>
                  <th className="px-4 py-4 text-base font-black whitespace-nowrap text-center">Rain</th>
                  <th className="px-4 py-4 text-base font-black whitespace-nowrap text-center">Humid</th>
                  <th className="px-4 py-4 text-base font-black whitespace-nowrap text-center">Wind</th>
                  <th className="px-4 py-4 text-base font-black whitespace-nowrap text-center">Low Cloud</th>
                  <th className="px-4 py-4 text-base font-black whitespace-nowrap text-center">Sun (h)</th>
                  <th className="px-2 py-4 text-base font-black whitespace-nowrap text-center">Dew Pt</th>
                  <th className="px-2 py-4 text-base font-black whitespace-nowrap text-center">SM. 10cm</th>
                  <th className="px-2 py-4 text-base font-black whitespace-nowrap text-center">SM. 40cm</th>
                  <th className="px-4 py-4 text-base font-black whitespace-nowrap text-center">ST. 10cm</th>
                </tr>
              </thead>

              {/* Table Body */}
              <tbody className="divide-y divide-black/10 dark:divide-white/10">
                {hourlyWeatherData?.map((hourlyTable, index) => (
                  <tr key={index} className="hover:bg-white/5 transition-all duration-200 group">
                    {/* Time */}
                    <td className="px-4 py-4">
                      <p className="text-lg font-bold text-blackPrimaryText dark:text-darkModeBlackPrimaryText whitespace-nowrap">
                        {hourlyTable.Date_time
                          ? extractTimeWithAMPM(hourlyTable.Date_time)
                          : "N/A"}
                      </p>
                    </td>

                    {/* Condition */}
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-4">
                        <div className="bg-white/10 p-2 rounded-xl border border-white/10 group-hover:scale-110 transition-transform flex-shrink-0">
                          <img
                            className="w-6 h-6 object-contain"
                            src={logoAndTitleFunction(hourlyTable).logo}
                            alt="Weather"
                          />
                        </div>
                        <span className="text-base font-bold text-blackPrimaryText dark:text-darkModeBlackPrimaryText leading-tight truncate">
                          {logoAndTitleFunction(hourlyTable).title || "N/A"}
                        </span>
                      </div>
                    </td>

                    {/* Avg Temp */}
                    <td className="px-4 py-4 text-center">
                      <p className="text-lg font-black text-blackPrimaryText dark:text-darkModeBlackPrimaryText whitespace-nowrap">
                        {safeTrunc(hourlyTable.Tavg)}°
                      </p>
                    </td>

                    {/* Min / Max Temp */}
                    <td className="px-4 py-4">
                      <div className="flex justify-center items-center gap-2">
                        <div className="text-center">
                          <p className="text-lg font-bold text-blue-500">{safeTrunc(hourlyTable.Tmin)}°</p>
                        </div>
                        <p className="text-lg font-bold text-blackPrimaryText dark:text-darkModeBlackPrimaryText">/</p>
                        <div className="text-center">
                          <p className="text-lg font-bold text-orange-500">{safeTrunc(hourlyTable.Tmax)}°</p>
                        </div>
                      </div>
                    </td>

                    {/* Rain */}
                    <td className="px-4 py-4 text-center">
                      <p className="text-lg font-bold text-blackPrimaryText dark:text-darkModeBlackPrimaryText whitespace-nowrap">
                        {safeFixed(hourlyTable.Rainfall)} <span className="text-sm opacity-50">mm</span>
                      </p>
                    </td>

                    {/* Humid */}
                    <td className="px-4 py-4 text-center">
                      <p className="text-lg font-bold text-blackPrimaryText dark:text-darkModeBlackPrimaryText whitespace-nowrap">
                        {safeTrunc(hourlyTable.RH)} <span className="text-sm opacity-50">%</span>
                      </p>
                    </td>

                    {/* Wind */}
                    <td className="px-4 py-4 text-center">
                      <p className="text-lg font-bold text-blackPrimaryText dark:text-darkModeBlackPrimaryText leading-none whitespace-nowrap">
                        {safeTrunc(hourlyTable.Wind_Speed)} <span className="text-sm opacity-50">m/s</span>
                      </p>
                      <p className="text-xs text-gray-400 font-bold mt-2">
                        {safeTrunc(hourlyTable.Wind_Direction)}°
                      </p>
                    </td>

                    {/* Cloud */}
                    <td className="px-4 py-4 text-center">
                      <p className="text-lg font-bold text-blackPrimaryText dark:text-darkModeBlackPrimaryText whitespace-nowrap">
                        {safePct(hourlyTable.Lowcloud, 0)} <span className="text-sm opacity-50">%</span>
                      </p>
                    </td>

                    {/* Sun */}
                    <td className="px-4 py-4 text-center">
                      <p className="text-lg font-bold text-blackPrimaryText dark:text-darkModeBlackPrimaryText whitespace-nowrap">
                        {hourlyTable.SunSD != null && !isNaN(hourlyTable.SunSD)
                          ? (hourlyTable.SunSD / 3600).toFixed(1)
                          : "N/A"}{" "}
                        <span className="text-sm opacity-50">h</span>
                      </p>
                    </td>

                    {/* Dew Pt */}
                    <td className="px-2 py-4 text-center">
                      <p className="text-lg font-bold text-blue-500 whitespace-nowrap">
                        {safeTrunc(hourlyTable.DPT)}°
                      </p>
                    </td>

                    {/* SM. 10cm */}
                    <td className="px-2 py-4 text-center">
                      <p className="text-lg font-bold text-blackPrimaryText dark:text-darkModeBlackPrimaryText whitespace-nowrap">
                        {safePct(hourlyTable.Soilm10)} <span className="text-sm opacity-50">%</span>
                      </p>
                    </td>

                    {/* SM. 40cm */}
                    <td className="px-2 py-4 text-center">
                      <p className="text-lg font-bold text-blackPrimaryText dark:text-darkModeBlackPrimaryText whitespace-nowrap">
                        {safePct(hourlyTable.Soilm40)} <span className="text-sm opacity-50">%</span>
                      </p>
                    </td>

                    {/* Soil T */}
                    <td className="px-4 py-4 text-center">
                      <div className="flex flex-col items-center">
                        <p className="text-lg font-bold text-blackPrimaryText dark:text-darkModeBlackPrimaryText whitespace-nowrap">
                          {safeFixed(hourlyTable.Soilt10)}°
                        </p>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default HourlyForcastTable;
